import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Task, TaskFormData } from '@/types/tasks';
import { useToast } from './useToast';
import { useProjectStore } from '@/store/projectStore';

export const useTaskManagement = (substageId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { fetchProjectDetails, fetchProjects } = useProjectStore();

  // Helper function to get project ID from substage
  const getProjectId = async (substageId: string | null): Promise<string | null> => {
    if (!substageId) return null;
    
    const { data, error } = await supabase
      .from('substages')
      .select('stage:stage_id(project_id)')
      .eq('id', substageId)
      .single();

    if (error || !data?.stage?.project_id) return null;
    return data.stage.project_id;
  };
  
  const createTask = async (data: TaskFormData) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('tasks')
        .insert([{
          substage_id: substageId || null, // Make substage_id optional
          title: data.title,
          description: data.description,
          priority: data.priority,
          start_date: data.start_date,
          end_date: data.end_date,
          assignee_id: data.assignee_id,
          status: 'pending'
        }]);

      if (error) throw error;

      // If we have a substage, refresh project details
      if (substageId) {
        const projectId = await getProjectId(substageId);
        if (projectId) {
          await fetchProjectDetails(projectId);
        }
      } else {
        // Otherwise refresh all projects to get the new task
        await fetchProjects();
      }

      toast({
        title: "Success",
        description: "Task created successfully"
      });
    } catch (err) {
      console.error('Failed to create task:', err);
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive"
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTask = async (taskId: string, data: Partial<Task>) => {
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('tasks')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (error) throw error;

      // If we have a substage, refresh project details
      if (substageId) {
        const projectId = await getProjectId(substageId);
        if (projectId) {
          await fetchProjectDetails(projectId);
        }
      } else {
        // Otherwise refresh all projects to get the updated task
        await fetchProjects();
      }

      toast({
        title: "Success",
        description: "Task updated successfully"
      });
    } catch (err) {
      console.error('Failed to update task:', err);
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive"
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      setIsLoading(true);

      // Get project ID before deleting if we have a substage
      const projectId = substageId ? await getProjectId(substageId) : null;
      
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      // If we have a project ID, refresh project details
      if (projectId) {
        await fetchProjectDetails(projectId);
      } else {
        // Otherwise refresh all projects
        await fetchProjects();
      }

      toast({
        title: "Success",
        description: "Task deleted successfully"
      });
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive"
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    createTask,
    updateTask,
    deleteTask
  };
};